/** Cells for the bookings grid's trip columns that every row carries (D5): the trip's start
 *  and end in its own zone, the end type, the zone, the length, the phase and who the
 *  person is on the booking. The expansion columns' cells live in `tripExpansionCells`. */
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { Box, Tooltip, Typography } from '@mui/material';

import type { BookingListItem, BookingParticipantKind, BookingTripPhase } from '@/api/bookings';
import { CellActionsPopover } from '@/components/DataGrid/CellActionsPopover';
import { TimestampValueCell } from '@/components/DataGrid/TimestampsCell';
import { TripPhaseLabel } from '@/features/records/time/TripPhaseLabel';
import { formatZoned } from '@/utils/zonedTime';

/** The caption under a zoned time: the zone the time is read in. With no known zone the
 *  time falls back to UTC, and says so. */
export function tripZoneCaption(row: Pick<BookingListItem, 'timezone'>): string {
  return row.timezone ?? 'UTC · tour zone unknown';
}

type InstantReplaceFn = (
  ns: 'start' | 'end',
  cond: { operator: 'range'; value: [string, string] } | null,
) => void;

/** Trip start / Trip end: the instant in the trip's zone (no time of day for an all-day
 *  booking), the zone named under it, and the ±delta popover over the `start` / `end`
 *  namespace with the UTC instant as its title. */
export function TripTimeCell({
  ns,
  iso,
  row,
  onReplaceFilter,
}: {
  ns: 'start' | 'end';
  iso: string | null;
  row: Pick<BookingListItem, 'timezone' | 'all_day'>;
  onReplaceFilter?: InstantReplaceFn | undefined;
}) {
  if (!iso) return <>—</>;
  return (
    <Box sx={{ py: 0.75, minWidth: 0 }}>
      <Box sx={{ whiteSpace: 'nowrap' }}>
        <TimestampValueCell ns={ns} iso={iso} onReplaceFilter={onReplaceFilter}>
          {formatZoned(iso, row.timezone, { zone: false, time: !row.all_day })}
        </TimestampValueCell>
      </Box>
      <Typography component="div" variant="caption" color="text.secondary" noWrap>
        {tripZoneCaption(row)}
      </Typography>
    </Box>
  );
}

/** End type in a word; the tooltip carries the backend's sentence. */
export const END_TYPE_LABEL: Record<0 | 1, string> = { 0: 'Itinerary', 1: 'Per booking' };

export function EndTypeCell({ row }: { row: Pick<BookingListItem, 'end_ts_type' | 'end_ts_type_display'> }) {
  return (
    <Tooltip title={row.end_ts_type_display} placement="top" arrow>
      <Box component="span">{END_TYPE_LABEL[row.end_ts_type]}</Box>
    </Tooltip>
  );
}

export function TimezoneCell({ row }: { row: Pick<BookingListItem, 'timezone'> }) {
  if (row.timezone) return <>{row.timezone}</>;
  return (
    <Tooltip title="No zone could be determined for the tour; its times show in UTC." placement="top" arrow>
      <Box component="span" sx={{ color: 'text.secondary' }}>
        Unknown
      </Box>
    </Tooltip>
  );
}

type PhaseReplaceFn = (ns: 'phase', cond: { operator: 'iexact'; value: string } | null) => void;

/** The phase (bar + word) with a popover that pins the grid to it (`phase=<value>`) or
 *  drops that filter. A booking has one phase, so the filter replaces, never stacks. */
export function PhaseCell({
  phase,
  onReplaceFilter,
}: {
  phase: BookingTripPhase;
  onReplaceFilter?: PhaseReplaceFn | undefined;
}) {
  const label = <TripPhaseLabel phase={phase} />;
  if (!onReplaceFilter) return label;
  return (
    <CellActionsPopover
      auto
      actions={[
        {
          key: 'filter',
          label: `Filter by this phase (phase=${phase})`,
          icon: <FilterAltIcon fontSize="small" />,
          onClick: () => onReplaceFilter('phase', { operator: 'iexact', value: phase }),
        },
        {
          key: 'reset',
          label: 'Reset phase filter',
          icon: <FilterAltOffIcon fontSize="small" />,
          onClick: () => onReplaceFilter('phase', null),
        },
      ]}
    >
      {label}
    </CellActionsPopover>
  );
}

export const PARTICIPANT_LABEL: Record<BookingParticipantKind, string> = {
  guest: 'Guest',
  guide: 'Guide',
  staff: 'Staff',
  observer: 'Observer',
};
